import { useContext, useMemo } from 'react'
import { useCurrentFrame, useVideoConfig } from 'remotion'
import {
  TextEditorSlotContext,
  TextItemEditingContext,
  TextItemHoverPreviewContext,
  type TextItemHoverPreview,
} from '../contexts'
import { useFadeOpacity } from '../fades'
import { turnFontStyleIntoCss, useLoadFont } from '../fonts'
import type { TextItem } from '../types'
import {
  INACTIVE_SPEAKER_OPACITY,
  useCurrentSpeaker,
} from '../useCurrentSpeaker'

/** The item as the inspector's hover preview would show it (editor only). */
export function overrideTextItemWithHoverPreview({
  textItem,
  hoverPreview,
}: {
  textItem: TextItem
  hoverPreview: TextItemHoverPreview | null
}): TextItem {
  if (!hoverPreview || hoverPreview.itemId !== textItem.id) return textItem
  if (hoverPreview.type === 'font-family') {
    return textItem.fontFamily === hoverPreview.fontFamily
      ? textItem
      : { ...textItem, fontFamily: hoverPreview.fontFamily }
  }
  return textItem.fontStyle.variant === hoverPreview.fontStyle.variant &&
    textItem.fontStyle.weight === hoverPreview.fontStyle.weight
    ? textItem
    : { ...textItem, fontStyle: hoverPreview.fontStyle }
}

export function TextLayer({
  item: itemWithoutHoverPreview,
}: {
  item: TextItem
}) {
  const frame = useCurrentFrame()
  const { fps, durationInFrames } = useVideoConfig()

  const hoverPreview = useContext(TextItemHoverPreviewContext)
  const item = useMemo(
    () =>
      overrideTextItemWithHoverPreview({
        textItem: itemWithoutHoverPreview,
        hoverPreview,
      }),
    [itemWithoutHoverPreview, hoverPreview],
  )

  const opacity = useFadeOpacity({
    frame,
    fps,
    durationInFrames,
    fadeInDurationInSeconds: item.fadeInDurationInSeconds,
    fadeOutDurationInSeconds: item.fadeOutDurationInSeconds,
    opacity: item.opacity,
  })

  const textItemEditing = useContext(TextItemEditingContext)
  const TextEditor = useContext(TextEditorSlotContext)

  // Zealous: a dynamic text shows whoever is speaking in its scene, and
  // nothing when nobody is — like captions with no words. `item.text` is
  // not a fallback: it is empty on a dynamic text.
  // Pinned to a participant (#566), it names them throughout and dims while
  // somebody else is talking.
  const dynamic = item.zealous?.dynamic
  const { speaker, active } = useCurrentSpeaker(
    dynamic ? item.zealous?.sceneId : undefined,
    dynamic?.forTrackId,
    item.from,
    dynamic?.participantId,
  )
  const text = dynamic ? (speaker?.[dynamic.field] ?? '') : item.text

  useLoadFont({
    fontFamily: item.fontFamily,
    fontVariant: item.fontStyle.variant,
    fontWeight: item.fontStyle.weight,
  })

  if (item.id === textItemEditing && TextEditor) {
    return <TextEditor item={item} />
  }

  return (
    <div
      dir={item.direction}
      style={{
        fontSize: item.fontSize,
        color: item.color,
        lineHeight: String(item.lineHeight),
        letterSpacing: `${item.letterSpacing}px`,
        left: item.left,
        top: item.top,
        width: item.width,
        height: item.height,
        position: 'absolute',
        whiteSpace: 'pre-wrap',
        display: 'block',
        fontFamily: item.fontFamily,
        ...turnFontStyleIntoCss(item.fontStyle),
        overflow: 'visible',
        wordWrap: 'break-word',
        boxSizing: 'border-box',
        userSelect: 'none',
        textAlign: item.align,
        opacity: active ? opacity : opacity * INACTIVE_SPEAKER_OPACITY,
        transform: `rotate(${item.rotation}deg)`,
        WebkitTextStroke: item.strokeWidth
          ? `${item.strokeWidth}px ${item.strokeColor}`
          : '0',
        paintOrder: 'stroke',
        // Zealous: a line clamp (#546), as the v1 layouts cut a long title
        // at the lines its row held.
        ...(item.maxLines
          ? {
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical' as const,
              WebkitLineClamp: item.maxLines,
              overflow: 'hidden',
            }
          : {}),
      }}
    >
      {/* Zealous: the Starter's text background, as per-line boxes (it drew
          them with @remotion/rounded-text-box, which we do not ship). */}
      {item.background && text ? (
        <span
          style={{
            backgroundColor: item.background.color,
            padding: `0 ${item.background.horizontalPadding}px`,
            borderRadius: item.background.borderRadius,
            boxDecorationBreak: 'clone',
            WebkitBoxDecorationBreak: 'clone',
          }}
        >
          {text}
        </span>
      ) : (
        text
      )}
    </div>
  )
}
